import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './SetPassword.module.css';
import routes from 'routes';
import {
  setPassword as changePassword,
  getMemberInfoForSetPassword,
} from 'api/authApi';
import CustomInput from 'components/common/CustomInput/CustomInput';
import CustomButton, {
  defaultButtonStyle,
} from 'components/common/CustomButton';
import MetaTags from 'components/common/MetaTags';

const SetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const email = searchParams.get('email');

  const [loading, setLoading] = useState(false);
  const [memberInfo, setMemberInfo] = useState(null);
  const [needsNameCollection, setNeedsNameCollection] = useState(false);
  const [loadingMemberInfo, setLoadingMemberInfo] = useState(true);

  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    password: '',
    repeat_password: '',
  });

  // Clear autofilled values on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setFormValues((prev) => ({
        firstName: prev.firstName, // Keep name values if set
        lastName: prev.lastName, // Keep name values if set
        password: '', // Clear password fields
        repeat_password: '', // Clear password fields
      }));
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const [errors, setErrors] = useState({});

  // Fetch member info on component mount
  useEffect(() => {
    const fetchMemberInfo = async () => {
      if (!email) {
        toast.error('Invalid email parameter');
        navigate(routes.auth.login);
        return;
      }

      try {
        setLoadingMemberInfo(true);

        // Try to decode email if it's base64 encoded, otherwise use as-is
        let decodedEmail = email;
        try {
          // Check if email looks like base64 (no @ symbol) and try to decode
          if (!email.includes('@')) {
            decodedEmail = atob(email);
          }
        } catch (decodeError) {
          // If decode fails, use email as-is
          console.log('Email not base64 encoded, using as plain text:', email);
          decodedEmail = email;
        }

        const response = await getMemberInfoForSetPassword(decodedEmail);
        const memberData = response.data;

        setMemberInfo(memberData);

        // Check if firstName or lastName is missing/empty
        const needsName = !memberData.firstName || !memberData.lastName;
        setNeedsNameCollection(needsName);

        // Pre-populate form if name exists
        if (!needsName) {
          setFormValues((prev) => ({
            ...prev,
            firstName: memberData.firstName || '',
            lastName: memberData.lastName || '',
          }));
        }
      } catch (error) {
        console.error('Failed to fetch member info:', error);
        toast.error('Failed to load member information');
        // Could navigate back to login or show error state
      } finally {
        setLoadingMemberInfo(false);
      }
    };

    fetchMemberInfo();
  }, [email, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation only if collection is needed
    if (needsNameCollection) {
      if (!formValues.firstName?.trim()) {
        newErrors.firstName = 'First name is required';
      }
      if (!formValues.lastName?.trim()) {
        newErrors.lastName = 'Last name is required';
      }
    }

    // Password validation
    if (!formValues.password) {
      newErrors.password = 'Password is required';
    } else if (formValues.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    } else if (!/(?=.*\d)/.test(formValues.password)) {
      newErrors.password = "Passwords must have at least one digit ('0'-'9')";
    } else if (!/(?=.*[a-z])/.test(formValues.password)) {
      newErrors.password =
        "Passwords must have at least one lowercase ('a'-'z')";
    } else if (!/(?=.*[A-Z])/.test(formValues.password)) {
      newErrors.password =
        "Passwords must have at least one uppercase ('A'-'Z')";
    }

    if (formValues.password !== formValues.repeat_password)
      newErrors.repeat_password = 'Passwords do not match';

    return newErrors;
  };

  const setPassword = async () => {
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      setLoading(true);
      try {
        // Try to decode email if it's base64 encoded, otherwise use as-is
        let decodedEmail = email;
        try {
          if (!email.includes('@')) {
            decodedEmail = atob(email);
          }
        } catch (decodeError) {
          decodedEmail = email;
        }

        // Prepare arguments for API call
        const firstName = needsNameCollection
          ? formValues.firstName.trim()
          : null;
        const lastName = needsNameCollection
          ? formValues.lastName.trim()
          : null;

        await changePassword(
          decodedEmail,
          formValues.password,
          firstName,
          lastName
        );

        const message = needsNameCollection
          ? 'Account profile completed successfully.'
          : 'Password set successfully.';

        toast.success(message);
        navigate(routes.auth.login);
      } catch (e) {
        console.log('error: ', e);
        toast.error(
          e.response?.data?.title ||
            e.response?.data ||
            'Failed to set password'
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const getHeaderText = () => {
    if (needsNameCollection) {
      return 'Complete Your Account Profile';
    }
    if (memberInfo?.firstName) {
      return `Welcome, ${memberInfo.firstName}!`;
    }
    return 'Set Your Password';
  };

  const getSubtitleText = () => {
    if (needsNameCollection) {
      return 'Please provide your name and create a secure password for your account.';
    }
    if (memberInfo?.firstName) {
      return `Please create a secure password to complete your account setup.`;
    }
    return 'Complete your registration by creating a secure password for your account.';
  };

  // Show loading state while fetching member info
  if (loadingMemberInfo) {
    return (
      <>
        <MetaTags title="SplitFast | Set password" />
        <div className={styles.setPasswordContainer}>
          <div className={styles.headerSection}>
            <h1 className="heading1">Loading...</h1>
            <p className={styles.subtitle}>
              Please wait while we load your information.
            </p>
          </div>
        </div>
      </>
    );
  }

  const decodedEmail = (() => {
    try {
      return email && !email.includes('@') ? atob(email) : email;
    } catch {
      return email;
    }
  })();

  return (
    <>
      <MetaTags title="SplitFast | Set password" />
      <div className={styles.setPasswordContainer}>
        <div className={styles.headerSection}>
          <h1 className="heading1">{getHeaderText()}</h1>
          <p className={styles.subtitle}>{getSubtitleText()}</p>
        </div>

        {/* Show account info if available but not collecting */}
        {!needsNameCollection &&
          memberInfo &&
          (memberInfo.firstName || memberInfo.lastName) && (
            <div className={styles.accountInfoDisplay}>
              <label className={styles.readOnlyLabel}>Account</label>
              <div className={styles.readOnlyValue}>
                {memberInfo.firstName} {memberInfo.lastName}
              </div>
              <label className={styles.readOnlyLabel}>Email</label>
              <div className={styles.readOnlyValue}>{decodedEmail}</div>
            </div>
          )}

        <div className={styles.inputSection}>
          {/* Show email as regular field if collecting names, or if no member info */}
          {(needsNameCollection || !memberInfo) && (
            <div className={styles.emailField}>
              <label className={styles.readOnlyLabel}>Email Address</label>
              <div className={styles.readOnlyValue}>{decodedEmail}</div>
            </div>
          )}

          {/* Name input fields if collection is needed */}
          {needsNameCollection && (
            <div className={styles.nameInputs}>
              <CustomInput
                label="First Name"
                name="firstName"
                type="text"
                placeholder="Enter your first name"
                value={formValues.firstName}
                error={errors.firstName}
                onChange={handleInputChange}
                required
                autoComplete="off"
                style={{ marginBottom: 20 }}
                inputStyle={{
                  fontSize: '15px',
                  height: '60px',
                }}
              />
              <CustomInput
                label="Last Name"
                name="lastName"
                type="text"
                placeholder="Enter your last name"
                value={formValues.lastName}
                error={errors.lastName}
                onChange={handleInputChange}
                required
                autoComplete="off"
                style={{ marginBottom: 20 }}
                inputStyle={{
                  fontSize: '15px',
                  height: '60px',
                }}
              />
            </div>
          )}

          {/* Hidden dummy fields to confuse browser autofill */}
          <input
            type="text"
            style={{ display: 'none' }}
            autoComplete="username"
            tabIndex="-1"
          />
          <input
            type="password"
            style={{ display: 'none' }}
            autoComplete="current-password"
            tabIndex="-1"
          />

          <CustomInput
            label="Password"
            name="password"
            type="password"
            placeholder="Type password"
            value={formValues.password}
            error={errors.password}
            onChange={handleInputChange}
            autoComplete="new-password"
            style={{ marginBottom: 20 }}
            inputStyle={{
              fontSize: '15px',
              height: '60px',
            }}
          />
          <CustomInput
            label="Repeat Password"
            name="repeat_password"
            type="password"
            placeholder="Confirm password"
            value={formValues.repeat_password}
            error={errors.repeat_password}
            onChange={handleInputChange}
            autoComplete="new-password"
            style={{ marginBottom: 20 }}
            inputStyle={{
              fontSize: '15px',
              height: '60px',
            }}
          />
        </div>

        <div className={styles.helpText}>
          <p>
            <strong>Password Requirements:</strong> Your password must be at
            least 8 characters long and contain at least 1 uppercase letter, 1
            lowercase letter, and 1 digit to ensure account security.
          </p>
        </div>

        <div className={styles.buttonSection}>
          <CustomButton
            disabled={loading}
            onClick={setPassword}
            style={{
              ...defaultButtonStyle,
              width: '100%',
              maxWidth: 400,
            }}
          >
            {loading
              ? needsNameCollection
                ? 'Completing profile...'
                : 'Setting password...'
              : needsNameCollection
                ? 'Complete Profile'
                : 'Set Password'}
          </CustomButton>
        </div>
      </div>
    </>
  );
};

export default SetPassword;
